import React, { useState, useEffect, useRef } from 'react';
import { Search, SortAsc, SortDesc } from 'lucide-react';


// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';


const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: 'white',
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const Dialog = ({ message, onClose, onCancel }) => {
  const isConfirmation = message.startsWith('Are you sure');
  const isEmptyApprovals = message.startsWith('No existing approvals found');

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div style={DialogStyles.overlay}>
      <div style={{
        ...DialogStyles.dialog,
        borderRadius: isEmptyApprovals ? '16px' : '8px',
        border: isEmptyApprovals ? '1px solid #ccc' : 'none',
      }}>
        <h2 style={{
          ...DialogStyles.title,
          fontSize: isEmptyApprovals ? '16.8px' : '18px',
          lineHeight: isEmptyApprovals ? '1.4' : '1.2',
        }}>
          {formattedMessage}
        </h2>
        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...DialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...DialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={{
              ...DialogStyles.button,
              backgroundColor: isEmptyApprovals ? '#FFFDD0' : '#FFFDD0',
              width: isEmptyApprovals ? '120px' : '100%',
              margin: isEmptyApprovals ? '0 auto' : '0',
              display: 'block'
            }}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

const ApproveRequirementsDialog = ({
  isOpen,
  onClose,
  selectedRequirements,
  selectedLevelId,
  corporateAccount,
  currentProject,
  login_token,
  functionalLevelDescription,
  requirementIdWithPrefix,
  req_type,
  canEdit
}) => {
  const [approvalLevel, setApprovalLevel] = useState('Requirement');
  const [approvalStatuses, setApprovalStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [comments, setComments] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingApprovals, setExistingApprovals] = useState([]);
  const [isLoadingApprovals, setIsLoadingApprovals] = useState(false);
  const [sortCriteria, setSortCriteria] = useState('updated_date desc');
  const [sortColumn, setSortColumn] = useState('updated_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApprovals, setSelectedApprovals] = useState(new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const firstInitRef = useRef(true);
  const commentsRef = useRef(null);
const [needsParentRefresh, setNeedsParentRefresh] = useState(false);

  console.log('In ApproveRequirementsDialog: selectedRequirements:', selectedRequirements);
  console.log('req id with prefix:', requirementIdWithPrefix);

  const showErrorDialog = (message) => {
    setDialogMessage(message);
    setShowDialog(true);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
  };

  const handleDialogCancel = () => {
    setShowDialog(false);
  };

  // Helper function to ensure data is fully reset
  const resetAllData = () => {
    setExistingApprovals([]);
    setSelectedApprovals(new Set());
    setError('');
    setIsLoadingApprovals(false);
    setSearchTerm('');
    setComments('');
    setSelectedStatus('');
  };




useEffect(() => {
  const initializeDialog = async () => {
    if (isOpen && !isInitialized) {
      console.log('🔄 Dialog opening, initializing...');

      // Reset all data
      resetAllData();
      setIsLoading(true);
      setNeedsParentRefresh(false); // Reset refresh flag

      await fetchApprovalStatuses();

      // Determine the initial approval level based on req_type
      let initialLevel = 'Project'; // Default fallback

      if (req_type) {
        switch (req_type.toUpperCase()) {
          case 'PROJECT LEVEL':
            initialLevel = 'Project';
            break;
          case 'FUNCTIONAL LEVEL':
            initialLevel = 'FunctionalLevel';
            break;
          case 'REQUIREMENT':
          case 'INTEGRATION REQUIREMENT':
            initialLevel = 'Requirement';
            break;
          default:
            if (selectedRequirements && selectedRequirements.size > 0) {
              initialLevel = 'Requirement';
            } else if (selectedLevelId) {
              initialLevel = 'FunctionalLevel';
            } else {
              initialLevel = 'Project';
            }
        }
      } else {
        if (selectedRequirements && selectedRequirements.size > 0) {
          initialLevel = 'Requirement';
        } else if (selectedLevelId) {
          initialLevel = 'FunctionalLevel';
        } else {
          initialLevel = 'Project';
        }
      }

      console.log('🎯 Setting initial approval level to:', initialLevel);
      setApprovalLevel(initialLevel);
      setIsInitialized(true);
    }
  };

  initializeDialog();
}, [isOpen]); // Only depend on dialog opening




useEffect(() => {
  if (!isOpen && isInitialized) {
    console.log('🔒 Dialog closed, resetting...');
    setIsInitialized(false);
    setNeedsParentRefresh(false);
  }
}, [isOpen, isInitialized]);


  // Fetch existing approvals when approval level changes or sort criteria changes
  useEffect(() => {
    const handleLevelChange = async () => {
      if (isOpen && isInitialized && approvalLevel) {
        console.log(`Fetching approvals for level: ${approvalLevel} with sort criteria: ${sortCriteria}`);
        setExistingApprovals([]);
        setSelectedApprovals(new Set());
        setError('');
        setIsLoadingApprovals(true);

        await fetchExistingApprovals();
      }
    };

    handleLevelChange();
  }, [approvalLevel, isOpen, isInitialized]);

  // Effect to handle search term changes - debounced search
  useEffect(() => {
    if (isOpen && isInitialized) {
      const debounceTimer = setTimeout(() => {
        fetchExistingApprovals();
      }, 500); // 500ms debounce

      return () => clearTimeout(debounceTimer);
    }
  }, [searchTerm, sortCriteria, isOpen, isInitialized, approvalLevel]);

  const fetchApprovalStatuses = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: 'REQUIREMENT_APPROVAL',
          search_query: ''
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const statuses = data.entity_statuses.map(s => s.status);
        setApprovalStatuses(statuses);
        if (statuses.length > 0) {
          // Default to first status
          setSelectedStatus(statuses[0]);
        }
      } else {
        throw new Error(data.status_description || 'Failed to fetch approval statuses');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching approval statuses:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExistingApprovals = async () => {
    setIsLoadingApprovals(true);
    setExistingApprovals([]);

    try {
      const requestBody = {
        corporate_account: corporateAccount,
        project_id: currentProject,
        level_id: approvalLevel === 'FunctionalLevel' ? selectedLevelId : '',
        req_ids: approvalLevel === 'Requirement' ? Array.from(selectedRequirements) : [],
        filter_by_user: '', // Empty to get all users' approvals
        sort_criteria: sortCriteria,
        req_type: req_type
      };

      console.log('Fetching approvals with:', requestBody);

      const response = await fetch(appConfig.getUrl('/get_requirements_approval_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      if (data.status === 'Success') {
        let approvals = data.requirement_approval_list || [];

        // Apply search filter
        if (searchTerm) {
          approvals = approvals.filter(approval =>
            (approval.req_id_with_prefix && approval.req_id_with_prefix.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (approval.user_name && approval.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (approval.approval_user_id && approval.approval_user_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (approval.approval_status && approval.approval_status.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (approval.approver_comments && approval.approver_comments.toLowerCase().includes(searchTerm.toLowerCase()))
          );
        }

        setExistingApprovals(approvals);

        if (approvals.length === 0 && !searchTerm) {
          showErrorDialog('No existing approvals found.\nSubmit new approvals to get started.');
        }

        console.log('Fetched approvals:', approvals);
      } else {
        console.error('Failed to fetch approvals:', data.status_description);
      }
    } catch (err) {
      console.error('Error fetching approvals:', err);
    } finally {
      setIsLoadingApprovals(false);
    }
  };

  // Function to handle sorting when a column header is clicked
  const handleSort = (column) => {
    // If clicking the same column, toggle direction
    if (column === sortColumn) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      setSortCriteria(`${column} ${newDirection}`);
    } else {
      // If clicking a new column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
      setSortCriteria(`${column} asc`);
    }

    console.log(`In handleSort: Sorting by ${column} ${sortDirection === 'asc' ? 'desc' : 'asc'}`);
  };

  // Handle search input with Enter key support
  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = () => {
    // Trigger refresh of approvals list
    fetchExistingApprovals();
  };

const handleApprovalLevelChange = (newLevel) => {
  console.log('👤 USER manually changed approval level from', approvalLevel, 'to', newLevel);
  if (newLevel !== approvalLevel) {
    setApprovalLevel(newLevel);
  }
};

const handleCancel = () => {
  console.log('🚪 User clicked close/cancel');

  // Always call onClose to close the dialog
  // Pass the refresh flag to let parent know if it needs to refresh
  onClose(needsParentRefresh, false);
};

 const handleSubmit = async () => {
  console.log('📤 STARTING SUBMISSION with approval level:', approvalLevel);

  setIsLoading(true);
  setError('');

  // Validate approval level selection
  if (approvalLevel === 'FunctionalLevel' && !selectedLevelId) {
    setError('Please select a functional level');
    setIsLoading(false);
    return;
  }

  if (approvalLevel === 'Requirement' && selectedRequirements.size === 0) {
    setError('Please select at least one requirement');
    setIsLoading(false);
    return;
  }

  // Validate form inputs
  if (!selectedStatus) {
    setError('Please select an approval status');
    setIsLoading(false);
    return;
  }

  if (!comments.trim()) {
    setError('Please enter approval comments');
    setIsLoading(false);
    return;
  }

  // Get the current user ID from localStorage
  const userId = localStorage.getItem('user_id') || '';
  if (!userId) {
    setError('User information not found. Please log in again.');
    setIsLoading(false);
    return;
  }

  // Prepare the request body based on approval level
  const requestBody = {
    corporate_account: corporateAccount,
    project_id: currentProject,
    level_id: approvalLevel === 'FunctionalLevel' ? selectedLevelId : '',
    req_ids: approvalLevel === 'Requirement' ? Array.from(selectedRequirements) : [],
    approval_user_id: userId,
    approval_comments: comments,
    approval_status: selectedStatus
  };

  console.log('Submitting approval with data:', requestBody);

  try {
    const response = await fetch(appConfig.getUrl('/update_requirement_approval_status'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();
    if (data.status === 'Success') {
      console.log('✅ Approval submitted successfully');

      // Show success message
      setError('Approval submitted successfully!');
      setComments(''); // Clear comments field for next submission

      // Mark that parent needs refresh when dialog closes
      setNeedsParentRefresh(true);

      // Delay to show success message, then refresh local table only
      setTimeout(() => {
        setError('');
        // Only refresh the local approvals list - no parent refresh
        fetchExistingApprovals();
      }, 2000);

      console.log('📝 Local approvals list will be refreshed, parent will refresh on dialog close');
    } else {
      throw new Error(data.status_description || 'Failed to update approval status');
    }
  } catch (err) {
    setError(err.message);
    console.error('Error updating approval status:', err);
  } finally {
    setIsLoading(false);
  }
};



  // Handle Enter key in form fields
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Generate level title based on current selection
  const getLevelTitle = () => {
    if (approvalLevel === 'Project') {
      return 'Project Level Approvals';
    } else if (approvalLevel === 'FunctionalLevel') {
      return 'Functional Level Approvals' + (functionalLevelDescription ? ' - ' + functionalLevelDescription : '');
    } else {
      return 'Requirement Level Approvals' + (requirementIdWithPrefix ? ' - ' + requirementIdWithPrefix : '');
    }
  };

  const renderApprovalTable = () => {
    if (isLoadingApprovals) {
      return (
        <div className="flex-1 overflow-auto border border-gray-200 rounded-md">
          <div className="text-center py-8">Loading existing approvals...</div>
        </div>
      );
    }

    if (existingApprovals.length === 0) {
      return (
        <div className="flex-1 overflow-auto border border-gray-200 rounded-md">
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? 'No approvals found matching your search.' : 'No existing approvals found'}
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 overflow-auto border border-gray-200 rounded-md  ml-10 mr-10">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>


              <th className="py-3 px-4 text-left">
                <div className="flex items-center cursor-pointer" onClick={() => handleSort('req_id_with_prefix')}>
                  Req ID
                  {sortColumn === 'req_id_with_prefix' && (
                    sortDirection === 'asc' ? <SortAsc size={14} className="ml-1" /> : <SortDesc size={14} className="ml-1" />
                  )}
                </div>
              </th>


              <th className="py-3 px-4 text-left">
                <div className="flex items-center cursor-pointer" onClick={() => handleSort('approval_user_id')}>
                  Approver
                  {sortColumn === 'approval_user_id' && (
                    sortDirection === 'asc' ? <SortAsc size={14} className="ml-1" /> : <SortDesc size={14} className="ml-1" />
                  )}
                </div>
              </th>
              <th className="py-3 px-4 text-left">
                <div className="flex items-center cursor-pointer" onClick={() => handleSort('approval_status')}>
                  Status
                  {sortColumn === 'approval_status' && (
                    sortDirection === 'asc' ? <SortAsc size={14} className="ml-1" /> : <SortDesc size={14} className="ml-1" />
                  )}
                </div>
              </th>
              <th className="py-3 px-4 text-left">
                <div className="flex items-center cursor-pointer" onClick={() => handleSort('approver_comments')}>
                  Comments
                  {sortColumn === 'approver_comments' && (
                    sortDirection === 'asc' ? <SortAsc size={14} className="ml-1" /> : <SortDesc size={14} className="ml-1" />
                  )}
                </div>
              </th>
              <th className="py-3 px-4 text-left">
                <div className="flex items-center cursor-pointer" onClick={() => handleSort('updated_date')}>
                  Date
                  {sortColumn === 'updated_date' && (
                    sortDirection === 'asc' ? <SortAsc size={14} className="ml-1" /> : <SortDesc size={14} className="ml-1" />
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {existingApprovals.map((approval, index) => (
              <tr key={index} className={`border-b border-gray-200 hover:bg-gray-100 ${selectedApprovals.has(index) ? 'bg-blue-100' : ''}`}>


                <td className="py-3 px-4" style={{ fontSize: '80%' }}>
                  {approval.req_id_with_prefix || approval.req_id}
                </td>

                <td className="py-3 px-4" style={{ fontSize: '80%' }}>
                  {approval.user_name || approval.approval_user_id}
                </td>
                <td className="py-3 px-4" style={{ fontSize: '80%' }}>
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    approval.approval_status === 'Approved' ? 'bg-green-100 text-green-800' :
                    approval.approval_status === 'Rejected' ? 'bg-red-100 text-red-800' :
                    approval.approval_status === 'Approved with conditions' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {approval.approval_status}
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-gray-500 max-w-xs truncate" style={{ fontSize: '80%' }}>
                  {approval.approver_comments}
                </td>
                    <td className="py-3 px-4" style={{ fontSize: '80%' }}>
                      {approval.updated_date ?
                        `${new Date(approval.updated_date).toLocaleString('en-US', {
                          timeZone: 'America/New_York',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })} ET` :
                        ''
                      }
                    </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
<div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 ">
<div className="bg-white rounded-lg max-w-screen-xl w-full max-h-[90vh] flex flex-col overflow-hidden ">
 <h1 className="text-l font-bold text-black bg-[#CCCCFF] text-center flex items-center justify-center rounded-t-lg" style={{ height: '3em' }}> Approve Requirement </h1>

        {/* Approval Level Selection Box */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-medium mb-4">Please select the level from the below options:</h3>

          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="approvalLevel"
                className="h-5 w-5 mr-3"
                checked={approvalLevel === 'Project'}
                onChange={() => handleApprovalLevelChange('Project')}
              />
              <span>Project Level : {currentProject}</span>
            </label>

            <label className={`flex items-center ${!selectedLevelId ? 'opacity-60' : ''}`}>
              <input
                type="radio"
                name="approvalLevel"
                className="h-5 w-5 mr-3"
                checked={approvalLevel === 'FunctionalLevel'}
                onChange={() => handleApprovalLevelChange('FunctionalLevel')}
                disabled={!selectedLevelId}
              />
              <span>Functional Level : {selectedLevelId ? functionalLevelDescription : 'No level selected'}</span>
            </label>

            <label className={`flex items-center ${selectedRequirements.size === 0 ? 'opacity-60' : ''}`}>
              <input
                type="radio"
                name="approvalLevel"
                className="h-5 w-5 mr-3"
                checked={approvalLevel === 'Requirement'}
                onChange={() => handleApprovalLevelChange('Requirement')}
                disabled={selectedRequirements.size === 0}
              />
              <span>
                {selectedRequirements.size === 0 ? (
                  'Requirement : No requirement selected'
                ) : selectedRequirements.size === 1 ? (
                  `Requirement : ${requirementIdWithPrefix}`
                ) : (
                  `Requirements : ${selectedRequirements.size} requirements selected`
                )}
              </span>
            </label>
          </div>
        </div>



        {/* Title bar with search */}
        <div className="flex items-center mb-4 ml-10 mr-10 justify-between bg-blue-100 p-3 rounded">
          {/* Search bar */}
          <div className="flex items-center">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              placeholder="Search approvals..."
              className="p-2 pr-10 border rounded w-full"
            />
            <button
              onClick={handleSearch}
              className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Search size={18} />
            </button>

            {/* Sort Dropdown */}
            <div className="ml-2 relative">
              <select
                value={sortCriteria}
                onChange={(e) => setSortCriteria(e.target.value)}
                className="p-2 border rounded bg-white text-sm"
              >
                <option value="updated_date desc">Updated Date (Newest)</option>
                <option value="updated_date asc">Updated Date (Oldest)</option>
                <option value="req_id asc">Requirement (A-Z)</option>
                <option value="req_id desc">Requirement (Z-A)</option>
                <option value="approval_user_id asc">Approver (A-Z)</option>
                <option value="approval_user_id desc">Approver (Z-A)</option>
                <option value="approval_status asc">Status (A-Z)</option>
                <option value="approval_status desc">Status (Z-A)</option>
              </select>
            </div>
          </div>

          <h2 className="text-l font-bold text-blue-700 text-center flex-1">{getLevelTitle()}</h2>

          <div className="w-32"></div> {/* Spacer to balance the layout */}
        </div>

        {/* Existing Approvals Table */}
        {renderApprovalTable()}

        {/* Footer with selection count */}
        <div className="flex justify-between items-center mt-2 mb-4">
          <div className="text-sm text-gray-500">
            {selectedApprovals.size > 0 ? selectedApprovals.size + ' selected' : ''}
          </div>
        </div>



        {/* New Approval Section */}
        <div className="border-t pt-4 mt-2">
          <h3 className="text-base font-semibold text-blue-800 bg-[#CCCCFF] mb-3  ml-10 mr-10 ">Submit New Approval</h3>



        {/* Error/Success Messages */}
        {error && (
          <div className={'mb-4 p-2 rounded ' + (
            error.includes('successfully')
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-500'
          )}>
            {error}
          </div>
        )}


          <div className="grid grid-cols-2 gap-4 mb-4  ml-10 mr-10 ">
            <div>
              <label className="block text-sm font-medium mb-2">Approval Status: <span className="text-red-500">*</span> </label>


              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full p-2 border rounded"
                disabled={isLoading}
              >
                <option value="">Select approval status</option>
                {approvalStatuses.map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Approval Comments:<span className="text-red-500">*</span></label>
              <textarea
                ref={commentsRef}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full p-2 border rounded"
                rows={3}
                placeholder="Enter your comments regarding this approval"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="flex justify-between items-center mt-6 mb-4 ml-10 mr-10">
          <div></div> {/* Empty space for alignment */}

          <div className="flex gap-4">
            <button
              onClick={handleCancel}
              className="bg-[#FFFDD0] hover:bg-yellow-200 px-6 py-2 rounded-md"
              disabled={isLoading}
            >
              Close
            </button>

            <button
              className={`px-4 py-2 rounded ${
                !canEdit ? 'bg-gray-300 text-green-500' : 'bg-[#C8F4DE] hover:bg-green-300 text-green-800'

              }`}
              disabled={isLoading || !canEdit}
              onClick={handleSubmit}
            >
              {isLoading ? 'Submitting...' : 'Submit Approval'}
            </button>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {showDialog && (
        <Dialog
          message={dialogMessage}
          onClose={handleDialogClose}
          onCancel={handleDialogCancel}
        />
      )}
    </div>
  );
};

export default ApproveRequirementsDialog;